import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { nanoid } from 'nanoid';

import {
  createImageObject,
  createQuestion,
  FOLDER_ID_CUSTOM,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION_SIZE,
  QUESTION_TYPES,
  QUESTION_TYPES_CAN_CARRY_FORWARD,
  QUESTION_TYPES_CAN_RANDOMIZE_OPTIONS,
  QUESTION_TYPES_CAN_RANDOMIZE_SUB_QUESTIONS,
  QUESTION_TYPES_WITH_OPTIONS,
  SURVEY_TYPES,
  updateQuestion as updateQuestionApi,
  uploadImageToS3,
  VW_DISPLAY_ORDERS,
} from '@api/instant_survey';

import { GATES } from '@constants';
import { isEUDomain } from '@helpers';
import {
  Announcement,
  ArrowBendDownRightIcon,
  Button,
  ButtonText,
  ColumnsIcon,
  ErrorText,
  ExclamationTriangleIcon,
  IconButton,
  Layout,
  ModalV2 as Modal,
  OverlayTriggerTooltip,
  PencilV2Icon,
  PlusIcon,
  RepromptV2Icon,
  RichTextInput,
  sanitizeRichText,
  SerifXIcon,
  stripHtml,
} from '@utilities';

import Answer from './components/Answer';
import DynamicConceptImageControl from './components/DynamicConceptImageControl';
import EditableText from './components/EditableText';
import GridAnnouncement from './components/GridAnnouncement';
import MaxDiffAnnouncement from './components/MaxDiffAnnouncement';
import MaxDiffWarningAnnouncement from './components/MaxDiffWarningAnnouncement';
import QuestionProperties from './components/QuestionProperties';
import Sidebar from './components/Sidebar';
import SubQuestion from './components/SubQuestion';
import TypeUnselectedPlaceholder from './components/TypeUnselectedPlaceholder';
import VideoSurveyInstructions from './components/VideoSurveyInstructions';

import {
  ANSWER_MAX_LENGTH,
  availableQuestionVariables,
  canPipingResponseTypes,
  DEFAULT_QUESTION_PROPS_BY_TYPE,
  newItemAvailableQuestionVariables,
  NONE_OF_THE_ABOVE,
  NONE_OF_THE_ABOVE_EU,
  OTHER_PLEASE_SPECIFY,
  OTHER_PLEASE_SPECIFY_ALTERNATES,
  OTHER_PLEASE_SPECIFY_ALTERNATES_EU,
  OTHER_PLEASE_SPECIFY_EU,
  QUESTION_CONT_SUM_MAX_OPTIONS,
  QUESTION_GRID_MAX_COLUMNS,
  QUESTION_GRID_MAX_ROWS,
  QUESTION_MAX_DIFF_LABEL_LENGTH,
  QUESTION_MAX_DIFF_MAX_OPTIONS,
  QUESTION_MAX_DIFF_MIN_OPTIONS,
  QUESTION_MAX_LENGTH,
  QUESTION_RANK_MAX_OPTIONS,
  QUESTION_TURF_MAX_OPTIONS,
  VALIDATION_MESSAGES,
  VW_STANDARD_PROMPTS,
  VW_STANDARD_STEM,
} from './utilities/constants';

import { SurveyContext } from '../../../../utilities/context';
import {
  getQuestionCost,
  getQuestionsCost,
  validateSequentialMonadicQuestion,
} from '../../../../utilities/helpers';
import { getPipingQuestionNumbersById } from '../../utilities';
import { getSurveyQuestionsForPiping, prepExistingQuestion } from './utilities/helpers';

import styles from './_index.module.scss';

const subQuestionPipingRegex = /\{answer from q(\d+)\.(\d+)\}/g;

const MONADIC_IMAGE_DISABLED_TOOLTIP =
  'Images are not available for questions in a monadic section.';

const ModalInstantSurveyQuestion = ({
  allowedQuestionTypes,
  conceptCount,
  getQuestionCostForValidation,
  isDuplicate,
  isSequentialMonadicSubQuestion,
  onClose,
  onCreate,
  onUpdate,
  originalQuestion,
  pipingQuestionsMap,
  shouldPersistOnCreate,
  shouldPersistOnUpdate,
}) => {
  const {
    activeQuestions,
    currentQuestionCount,
    instantSurveyType,
    maxQuestions,
    questionDisplayIndexById,
    survey,
  } = useContext(SurveyContext);
  const [carryForwardLogic, setCarryForwardLogic] = useState(
    survey.carryForwardLogic?.[originalQuestion?.id] || null
  );
  const [deletedAnswers, setDeletedAnswers] = useState([]);
  const [deletedSubQuestions, setDeletedSubQuestions] = useState([]);
  const [errorSubmitting, setErrorSubmitting] = useState(null);
  const [hasImageError, setHasImageError] = useState({ answers: [], subQuestions: [] });
  const [isImageLoading, setIsImageLoading] = useState({ answers: [], subQuestions: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subQuestionDeleteError, setSubQuestionDeleteError] = useState(null);
  const prevTypeRef = useRef(undefined);

  const { client } = useStatsigClient();
  const canVanWestendorp = client.checkGate(GATES.VAN_WESTENDORP);
  const canVideo = client.checkGate(GATES.QUESTION_TYPE_VIDEO);
  const isVideoSurvey = instantSurveyType.id === SURVEY_TYPES.VIDEO;
  const requireQuestionTypeSelection = client.checkGate(GATES.REQUIRE_QUESTION_TYPE_SELECTION);
  const useCalypsoProvider = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);

  const availableQuestionTypes = [
    QUESTION_TYPES.CHECKBOX,
    QUESTION_TYPES.RADIO,
    QUESTION_TYPES.ESSAY,
    QUESTION_TYPES.INSTRUCTIONS,
    QUESTION_TYPES['RANK-DRAGDROP'],
    QUESTION_TYPES['TABLE-RADIO'],
    QUESTION_TYPES.TURF,
    QUESTION_TYPES.CONTSUM,
    QUESTION_TYPES.MAX_DIFF,
    QUESTION_TYPES.NUMERIC,
    ...(canVanWestendorp ? [QUESTION_TYPES.VAN_WESTENDORP] : []),
    ...(canVideo ? [QUESTION_TYPES.VIDEO] : []),
  ];

  const [question, setQuestion] = useState(
    originalQuestion
      ? prepExistingQuestion({
          availableQuestionTypes,
          isDuplicate,
          question: originalQuestion,
          shouldCreateFromOriginal:
            isDuplicate || (shouldPersistOnUpdate && originalQuestion.folder !== FOLDER_ID_CUSTOM),
        })
      : isVideoSurvey
        ? {
            answers: [{ id: nanoid(), text: '' }],
            image: null,
            noneAnswer: null,
            properties: {},
            richText: null,
            showConceptImage: false,
            subQuestions: [],
            text: '',
            type: QUESTION_TYPES.VIDEO.value,
          }
        : {
            answers: Array.from(new Array(3), () => ({
              id: nanoid(),
              richText: null,
              text: '',
            })),
            image: null,
            noneAnswer: null,
            properties: requireQuestionTypeSelection
              ? {}
              : { ...DEFAULT_QUESTION_PROPS_BY_TYPE[QUESTION_TYPES.CHECKBOX.value] },
            richText: null,
            showConceptImage: false,
            subQuestions: [],
            text: '',
            type: requireQuestionTypeSelection ? null : QUESTION_TYPES.CHECKBOX.value,
          }
  );

  const activeQuestionIndex = activeQuestions.findIndex(({ id }) => id === originalQuestion?.id);
  const answersLength = useMemo(
    () => question.answers.filter(({ isCarryForwardGenerated }) => !isCarryForwardGenerated).length,
    [question.answers]
  );
  const subQuestionsLength = useMemo(
    () =>
      question.subQuestions.filter(({ isCarryForwardGenerated }) => !isCarryForwardGenerated)
        .length,
    [question.subQuestions]
  );

  const questionsBeforeCurrent = useMemo(
    () =>
      activeQuestions.slice(
        0,
        isDuplicate || activeQuestionIndex === -1 ? undefined : activeQuestionIndex
      ),
    [activeQuestionIndex, activeQuestions, isDuplicate]
  );
  const currentQuestionNumber = getQuestionsCost(questionsBeforeCurrent) + 1;
  const currentQuestionType = useMemo(() => {
    return (
      Object.values(QUESTION_TYPES).find(({ value }) => value === question.type) ||
      QUESTION_TYPES.ESSAY
    );
  }, [question.type]);

  const isCheckbox = question.type === QUESTION_TYPES.CHECKBOX.value;
  const isCarryForwardSource =
    question.id &&
    Object.values(survey?.carryForwardLogic || {}).some(
      ({ sourceSurveyQuestionId }) => sourceSurveyQuestionId === question.id
    );
  const isContSum = question.type === QUESTION_TYPES.CONTSUM.value;
  const isEditing = originalQuestion && !isDuplicate;
  const isGridType = question.type === QUESTION_TYPES['TABLE-RADIO'].value;
  const isMaxDiff = question.type === QUESTION_TYPES.MAX_DIFF.value;
  const isRadio = question.type === QUESTION_TYPES.RADIO.value;
  const isRankType = question.type === QUESTION_TYPES['RANK-DRAGDROP'].value;
  const isTurf = question.type === QUESTION_TYPES.TURF.value;
  const isVanWestendorp = question.type === QUESTION_TYPES.VAN_WESTENDORP.value;
  const isVideo = question.type === QUESTION_TYPES.VIDEO.value;
  const isTypeUnselected = requireQuestionTypeSelection && !question.type;
  const hasOtherAnswer = Boolean(
    question?.answers.find(
      ({ isCarryForwardGenerated, isOther }) => !isCarryForwardGenerated && isOther
    )
  );

  // VW options are stored canonically (too cheap → too expensive); mirror the respondent-facing
  // order in the editor when the researcher picks Highest → lowest, without changing stored order.
  const orderedAnswers =
    isVanWestendorp && question.properties.displayOrder === VW_DISPLAY_ORDERS.HIGH_TO_LOW.value
      ? [...question.answers].reverse()
      : question.answers;

  const canAddNone = (isCheckbox || isRadio || isTurf) && !question.noneAnswer;
  const canAddOther = (isCheckbox || isRadio || isTurf) && !hasOtherAnswer;

  const noneOfTheAbove = isEUDomain() ? NONE_OF_THE_ABOVE_EU : NONE_OF_THE_ABOVE;
  const otherAlternates = isEUDomain()
    ? OTHER_PLEASE_SPECIFY_ALTERNATES_EU
    : OTHER_PLEASE_SPECIFY_ALTERNATES;
  const otherPleaseSpecify = isEUDomain() ? OTHER_PLEASE_SPECIFY_EU : OTHER_PLEASE_SPECIFY;

  const hasConceptImage =
    Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptImage != null);
  const hasConceptName =
    Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptName != null);

  useEffect(() => {
    const prevType = prevTypeRef.current;
    prevTypeRef.current = question.type;

    const wasGridType = prevType === QUESTION_TYPES['TABLE-RADIO'].value;
    const wasUndefinedType = prevType === undefined;

    if (isTypeUnselected) return;
    if (isGridType) {
      const carryForwardAnswers = question.answers.filter(
        ({ isCarryForwardGenerated }) => isCarryForwardGenerated
      );
      if (!wasGridType && !wasUndefinedType && carryForwardLogic) {
        if (carryForwardAnswers.length) handleDeleteAnswers(carryForwardAnswers);
        if (carryForwardLogic) setCarryForwardLogic(null);
      }

      setQuestion((prev) => ({
        ...prev,
        ...(prev.subQuestions.length === 0 && {
          subQuestions: Array.from(new Array(3), () => ({
            id: nanoid(),
            text: '',
            type: 'RADIO',
          })),
        }),
        ...(carryForwardAnswers.length && {
          answers: question.answers.filter(
            ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
          ),
        }),
      }));
    } else if (!isGridType && wasGridType) {
      setDeletedSubQuestions((prev) => [
        ...prev,
        ...question.subQuestions.filter(({ id }) => typeof id === 'number'),
      ]);
      updateQuestion({ subQuestions: [] });
      if (carryForwardLogic) setCarryForwardLogic(null);
    }

    if (!QUESTION_TYPES_CAN_CARRY_FORWARD.includes(question.type)) {
      if (carryForwardLogic) setCarryForwardLogic(null);
      const carryForwardAnswers = question.answers.filter(
        ({ isCarryForwardGenerated }) => isCarryForwardGenerated
      );
      if (carryForwardAnswers.length) handleDeleteAnswers(carryForwardAnswers);
      if (carryForwardAnswers.length) {
        updateQuestion({
          answers: question.answers.filter(
            ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
          ),
        });
      }
    }

    if (!isCheckbox && !isRadio && !isTurf) {
      deleteNoneAnswer();
      deleteOtherAnswer();
    }
  }, [question.type]);

  useEffect(() => {
    if (subQuestionDeleteError) {
      // Auto-clear the error after 5 seconds
      const timer = setTimeout(() => {
        setSubQuestionDeleteError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [subQuestionDeleteError]);

  const addAnswer = () => {
    setQuestion((question) => ({
      ...question,
      answers: [...question.answers, { id: nanoid(), richText: null, text: '' }],
    }));
  };

  const addSubQuestion = () => {
    setQuestion((question) => ({
      ...question,
      subQuestions: [...question.subQuestions, { id: nanoid(), text: '', type: 'RADIO' }],
    }));
  };

  const addNoneAnswer = () => {
    updateQuestion({
      noneAnswer: {
        id: nanoid(),
        isAnchored: true,
        isExclusive: true,
        isNoneOfTheAbove: true,
        text: noneOfTheAbove,
      },
    });
  };

  const addOtherAnswer = () => {
    setQuestion((question) => ({
      ...question,
      answers: [
        ...question.answers,
        {
          id: nanoid(),
          isAnchored: true,
          isOther: true,
          text: otherPleaseSpecify,
        },
      ],
    }));
  };

  const deleteAnswer = (index) => {
    const newAnswers = [...question.answers];
    const deletedAnswer = newAnswers.splice(index, 1)[0];
    handleDeleteAnswers([deletedAnswer]);
    updateQuestion({ answers: [...newAnswers] });
    updateMaxAnswersAfterDelete();
  };

  const deleteOtherAnswer = () => {
    const otherAnswerIndex = question?.answers.findIndex(({ isOther }) => isOther);
    if (otherAnswerIndex !== -1) {
      deleteAnswer(otherAnswerIndex);
    }
  };

  const deleteNoneAnswer = () => {
    if (question.noneAnswer) {
      handleDeleteAnswers([question.noneAnswer]);
      updateQuestion({ noneAnswer: null });
      updateMaxAnswersAfterDelete();
    }
  };

  const deleteSubQuestion = (index) => {
    if (isSubQuestionInPiping(index)) {
      setSubQuestionDeleteError(
        'This sub-question cannot be deleted because it is a piping source for another question. Remove the piping reference first, then delete this sub-question.'
      );
      return;
    }
    // check if removing any sub-question would affect piping targeting the last indexed sub-question
    if (isSubQuestionInPiping(question.subQuestions.length - 1)) {
      setSubQuestionDeleteError(
        'Sub-questions cannot be deleted due to a piping reference to the last sub-question. Remove the piping reference first, then delete this sub-question.'
      );
      return;
    }

    setSubQuestionDeleteError(null);
    const remainingSubQuestions = [...question.subQuestions];

    const deletedSubQuestion = remainingSubQuestions.splice(index, 1)[0];

    if (typeof deletedSubQuestion.id === 'number') {
      setDeletedSubQuestions((prev) => [...prev, { ...deletedSubQuestion }]);
    }
    updateQuestion({ subQuestions: [...remainingSubQuestions] });
  };

  const isSubQuestionInPiping = (index) => {
    const children = pipingQuestionsMap.childrenByParent[originalQuestion?.id] || [];
    return children.some(({ questionText }) => {
      const freshRegex = new RegExp(subQuestionPipingRegex); // reset regex state
      let match;
      while ((match = freshRegex.exec(questionText)) !== null) {
        const questionIndex = parseInt(match[1], 10);
        if (questionIndex !== currentQuestionNumber) continue;

        const subQuestionIndex = parseInt(match[2], 10) - 1;
        if (subQuestionIndex === index) return true;
      }
      return false;
    });
  };

  const getNewOptionTooltip = () => {
    if (isContSum && answersLength >= QUESTION_CONT_SUM_MAX_OPTIONS)
      return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
    if (isMaxDiff && answersLength >= QUESTION_MAX_DIFF_MAX_OPTIONS)
      return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
    if (isRankType && answersLength >= QUESTION_RANK_MAX_OPTIONS)
      return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
    return null;
  };

  const getSaveButtonTooltip = () => {
    if (isTypeUnselected) return VALIDATION_MESSAGES.NO_TYPE_SELECTED;
    if (!question.text.trim()) return VALIDATION_MESSAGES.QUESTION_TEXT_REQUIRED;
    if (
      QUESTION_TYPES_CAN_CARRY_FORWARD.includes(question.type) &&
      carryForwardLogic &&
      !carryForwardLogic.sourceSurveyQuestionId
    )
      return VALIDATION_MESSAGES.CARRY_FORWARD_SOURCE_REQUIRED;
    // pasting text into <MentionsInput /> can exceed its maxLength, these checks are still necessary
    // https://github.com/signavio/react-mentions/issues/616
    if (question.text.length > QUESTION_MAX_LENGTH)
      return VALIDATION_MESSAGES.QUESTION_MAX_LENGTH_ERROR;

    const normalizedAnswers = question.answers
      .filter(({ isCarryForwardGenerated, isOther }) => !isCarryForwardGenerated || !isOther)
      .map(({ text }) => text.trim().toLowerCase());
    if (question.noneAnswer)
      normalizedAnswers.push(question.noneAnswer.text?.trim()?.toLowerCase());

    const answerDuplicates = normalizedAnswers.filter(
      (text, index) => normalizedAnswers.indexOf(text) !== index
    );
    const duplicatesString = ` (${[...new Set(answerDuplicates)].join(', ')})`;

    if (isContSum) {
      if (answersLength > QUESTION_CONT_SUM_MAX_OPTIONS)
        return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
      if (question.noneAnswer || hasOtherAnswer)
        return `${QUESTION_TYPES.CONTSUM.name} cannot have ${otherPleaseSpecify} or ${noneOfTheAbove} options.`;
    }

    if (isTurf) {
      if (answersLength > QUESTION_TURF_MAX_OPTIONS)
        return VALIDATION_MESSAGES.TURF_MAX_OPTIONS_ERROR;
    }

    if (isGridType) {
      const normalizedSubQuestions = question.subQuestions.filter(
        ({ isCarryForwardGenerated }) => !isCarryForwardGenerated
      );

      if (answersLength === 0) return VALIDATION_MESSAGES.GRID_COLUMNS_REQUIRED;
      if (answersLength > QUESTION_GRID_MAX_COLUMNS)
        return VALIDATION_MESSAGES.GRID_MAX_COLUMNS_ERROR;
      if (question.answers.some(({ text }) => !text.trim()))
        return VALIDATION_MESSAGES.GRID_COLUMNS_BLANK_ERROR;
      if (answerDuplicates.length > 0)
        return VALIDATION_MESSAGES.GRID_COLUMNS_DUPLICATE_ERROR + duplicatesString;

      if (question.subQuestions.length === 0) return VALIDATION_MESSAGES.GRID_ROWS_REQUIRED;
      if (normalizedSubQuestions.length > QUESTION_GRID_MAX_ROWS)
        return VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR;
      if (normalizedSubQuestions.some(({ text }) => !text.trim()))
        return VALIDATION_MESSAGES.GRID_ROWS_BLANK_ERROR;
      if (normalizedSubQuestions.some(({ text }) => text.length > ANSWER_MAX_LENGTH))
        return VALIDATION_MESSAGES.GRID_ROW_LENGTH_ERROR;

      const normalizedSubQuestionTexts = normalizedSubQuestions.map(({ text }) =>
        text.trim().toLowerCase()
      );
      const subQuestionDuplicates = normalizedSubQuestionTexts.filter(
        (text, index) => normalizedSubQuestionTexts.indexOf(text) !== index
      );
      if (subQuestionDuplicates.length > 0)
        return (
          VALIDATION_MESSAGES.GRID_ROWS_DUPLICATE_ERROR +
          ` (${[...new Set(subQuestionDuplicates)].join(', ')})`
        );
    }

    if (isMaxDiff) {
      if (!question.properties.bestLabel?.trim())
        return VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_REQUIRED;
      if (question.properties.bestLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH)
        return VALIDATION_MESSAGES.MAX_DIFF_BEST_LABEL_LENGTH_ERROR;
      if (!question.properties.worstLabel?.trim())
        return VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_REQUIRED;
      if (question.properties.worstLabel?.length > QUESTION_MAX_DIFF_LABEL_LENGTH)
        return VALIDATION_MESSAGES.MAX_DIFF_WORST_LABEL_LENGTH_ERROR;
      if (answersLength < QUESTION_MAX_DIFF_MIN_OPTIONS)
        return VALIDATION_MESSAGES.MAX_DIFF_MIN_OPTIONS_ERROR;
      if (answersLength > QUESTION_MAX_DIFF_MAX_OPTIONS)
        return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
    }

    if (isRankType) {
      if (answersLength > QUESTION_RANK_MAX_OPTIONS)
        return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
      if (question.noneAnswer || hasOtherAnswer)
        return `${QUESTION_TYPES['RANK-DRAGDROP'].name} cannot have ${otherPleaseSpecify} or ${noneOfTheAbove} options.`;
    }

    if (isVanWestendorp) {
      if (question.answers.some(({ text }) => !text.trim()))
        return VALIDATION_MESSAGES.ANSWER_BLANK_ERROR;
      if (question.answers.some(({ text }) => text.length > ANSWER_MAX_LENGTH))
        return VALIDATION_MESSAGES.ANSWER_LENGTH_ERROR;
      const { maxNumber, minNumber } = question.properties;
      if (minNumber === undefined || maxNumber === undefined)
        return VALIDATION_MESSAGES.VW_PRICE_RANGE_REQUIRED;
      if (minNumber <= 0) return VALIDATION_MESSAGES.VW_MIN_PRICE_ERROR;
      if (maxNumber <= minNumber) return VALIDATION_MESSAGES.MAX_VALUE_MIN_VALUE_ERROR;
    }

    const sequentialMonadicValidationError = validateSequentialMonadicQuestion(question);
    if (sequentialMonadicValidationError) return sequentialMonadicValidationError;

    if (isCheckbox || isContSum || isMaxDiff || isGridType || isRadio || isRankType || isTurf) {
      if (question.answers.length === 0) return VALIDATION_MESSAGES.ANSWERS_REQUIRED;
      if (question.answers.some(({ text }) => !text.trim())) {
        return VALIDATION_MESSAGES.ANSWER_BLANK_ERROR;
      }
      if (question.answers.some(({ text }) => text.length > ANSWER_MAX_LENGTH))
        return VALIDATION_MESSAGES.ANSWER_LENGTH_ERROR;
      if (answerDuplicates.length > 0)
        return VALIDATION_MESSAGES.ANSWER_DUPLICATE_ERROR + duplicatesString;
    }

    if (
      question.properties.forcePercent &&
      (question.properties.maxNumber > 100 || question.properties.maxTotal > 100)
    )
      return VALIDATION_MESSAGES.MAX_PERCENTAGE_ERROR;
    if (question.properties.maxNumber > question.properties.maxTotal)
      return VALIDATION_MESSAGES.MAX_VALUE_MAX_TOTAL_ERROR;

    const currentQuestionCost = getQuestionCostForValidation(question);
    const originalQuestionCost = originalQuestion
      ? getQuestionCostForValidation(originalQuestion)
      : 0;
    if (
      currentQuestionCost + currentQuestionCount - (isEditing ? originalQuestionCost : 0) >
      maxQuestions
    ) {
      return VALIDATION_MESSAGES.QUESTION_COST_ERROR;
    }

    return null;
  };

  const getAnswerInputError = ({ answer, index }) => {
    if (answer.isCarryForwardGenerated) return null;

    const answerIndexWithoutCarryForward =
      question.answers
        .slice(0, index + 1)
        .filter(({ isCarryForwardGenerated }) => !isCarryForwardGenerated).length - 1;

    if (isContSum && answerIndexWithoutCarryForward >= QUESTION_CONT_SUM_MAX_OPTIONS)
      return VALIDATION_MESSAGES.CONTSUM_MAX_OPTIONS_ERROR;
    if (isMaxDiff && answerIndexWithoutCarryForward >= QUESTION_MAX_DIFF_MAX_OPTIONS)
      return VALIDATION_MESSAGES.MAX_DIFF_MAX_OPTIONS_ERROR;
    if (isRankType && answerIndexWithoutCarryForward >= QUESTION_RANK_MAX_OPTIONS)
      return VALIDATION_MESSAGES.RANK_MAX_OPTIONS_ERROR;
    if (answer.text?.length > ANSWER_MAX_LENGTH) return VALIDATION_MESSAGES.ANSWER_LENGTH_ERROR;
    if (!answer.text?.trim() && answer.cdnUrl) return VALIDATION_MESSAGES.ANSWER_BLANK_ERROR;
    return null;
  };

  const getSubQuestionError = ({ index, subQuestion }) => {
    const subQuestionIndexWithoutCarryForward =
      question.subQuestions
        .slice(0, index + 1)
        .filter(({ isCarryForwardGenerated }) => !isCarryForwardGenerated).length - 1;

    if (isGridType && subQuestionIndexWithoutCarryForward >= QUESTION_GRID_MAX_ROWS)
      return VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR;
    if (subQuestion.text?.length > ANSWER_MAX_LENGTH)
      return VALIDATION_MESSAGES.GRID_ROW_LENGTH_ERROR;
    return null;
  };

  const handleDeleteAnswers = (answersToDelete) => {
    setDeletedAnswers((prev) => [
      ...prev,
      ...answersToDelete.filter(({ id }) => typeof id === 'number'),
    ]);
  };

  const handleDeleteSubQuestions = (subQuestionsToDelete) => {
    setDeletedSubQuestions((prev) => [
      ...prev,
      ...subQuestionsToDelete.filter(({ id }) => typeof id === 'number'),
    ]);
  };

  const handleSelectQuestionType = (type) => {
    if (type === question.type) return;

    const onVanWestendorpQuestion =
      type === QUESTION_TYPES.VAN_WESTENDORP.value ||
      question.type === QUESTION_TYPES.VAN_WESTENDORP.value;
    if (onVanWestendorpQuestion) {
      handleDeleteAnswers([
        ...question.answers,
        ...(question.noneAnswer ? [question.noneAnswer] : []),
      ]);
    }

    setQuestion((prev) => ({
      ...prev,
      ...(prev.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value &&
        type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value && {
          concepts: [],
        }),
      ...(type === QUESTION_TYPES.VAN_WESTENDORP.value && {
        answers: VW_STANDARD_PROMPTS.map((text) => ({ id: nanoid(), richText: null, text })),
        noneAnswer: null,
        richText: null,
        text: VW_STANDARD_STEM,
      }),
      ...(prev.type === QUESTION_TYPES.VAN_WESTENDORP.value &&
        type !== QUESTION_TYPES.VAN_WESTENDORP.value && {
          answers: Array.from(new Array(3), () => ({ id: nanoid(), richText: null, text: '' })),
          richText: null,
          text: '',
        }),
      properties: {
        ...(DEFAULT_QUESTION_PROPS_BY_TYPE[type] || {}),
        ...(QUESTION_TYPES_CAN_RANDOMIZE_OPTIONS.includes(type) && {
          randomizeOptions: prev.properties.randomizeOptions ?? false,
        }),
        ...(QUESTION_TYPES_CAN_RANDOMIZE_SUB_QUESTIONS.includes(type) && {
          randomizeSubQuestions: prev.properties.randomizeSubQuestions ?? false,
        }),
      },
      type,
    }));
  };

  const handleUpdateQuestion = (change) => {
    setQuestion((prev) => ({ ...prev, ...change }));
  };

  const handleUpdateQuestionProperties = (change) => {
    setQuestion((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        ...change,
      },
    }));
  };

  const handleUpdateShowConceptImage = () =>
    setQuestion((prev) => ({
      ...prev,
      showConceptImage: !prev.showConceptImage,
    }));

  const onAnswerDragEnd = (result) => {
    if (!result?.destination) {
      return;
    }
    const answers = [...question.answers];
    const [removed] = answers.splice(result?.source?.index, 1);
    answers.splice(result?.destination?.index, 0, removed);
    updateQuestion({ answers });
  };

  /**
   * Converts pasted text into an array of lines
   * @param {string} text
   * @returns {string[]}
   */
  const convertPasteToValues = (text) => {
    return text
      .split(/[\n\r]+/) // split on newlines and returns
      .map((text) => text.trim().replace(/^[\s•*\-□]+/, '')) // trim whitespace and remove bullet points, checkboxes, and dashes
      .filter((n) => n); // filter removes empty string in cases of repeated line breaks
  };

  const onAnswerPaste = (event, value, index) => {
    const lines = convertPasteToValues(value);
    if (lines.length <= 1) return;
    if (isVanWestendorp) return;

    event.preventDefault();
    event.stopPropagation();
    let hasAddedOtherAnswer = false;
    let shouldAddNoneAnswer = false;

    const values = lines.reduce((answers, text) => {
      const normalizedText = text.toLowerCase();

      if (otherAlternates.includes(normalizedText)) {
        if (canAddOther && !hasAddedOtherAnswer) {
          hasAddedOtherAnswer = true;
          answers.push({
            id: nanoid(),
            isAnchored: true,
            isOther: true,
            text: otherPleaseSpecify,
          });
        }
        return answers;
      } else if (normalizedText === noneOfTheAbove.toLowerCase()) {
        if (canAddNone) shouldAddNoneAnswer = true;
        return answers;
      }

      answers.push({
        id: nanoid(),
        richText: null,
        text,
      });
      return answers;
    }, []);

    const answersClone = [...question.answers];
    const deletedItems = answersClone.splice(index, values.length, ...values);
    const noneAnswer = shouldAddNoneAnswer
      ? {
          id: nanoid(),
          isAnchored: true,
          isExclusive: true,
          isNoneOfTheAbove: true,
          text: noneOfTheAbove,
        }
      : null;

    handleDeleteAnswers(deletedItems);
    updateQuestion({
      answers: answersClone,
      ...(noneAnswer && { noneAnswer }),
    });
  };

  const onSubQuestionPaste = (event, value, index) => {
    const lines = convertPasteToValues(value);
    if (lines.length <= 1) return;

    event.preventDefault();

    const indexesToCheck = Array.from({ length: lines.length }, (_, i) => index + i);
    if (indexesToCheck.some((i) => isSubQuestionInPiping(i))) {
      setSubQuestionDeleteError(
        'Cannot paste over sub-questions due to piping logic in another question. Remove the piping reference first, then paste.'
      );
      return;
    }

    const newSubQuestions = lines.map((text) => ({
      id: nanoid(),
      text,
      type: 'RADIO',
    }));

    const subQuestionsClone = [...question.subQuestions];
    const deletedItems = subQuestionsClone.splice(
      index,
      newSubQuestions.length,
      ...newSubQuestions
    );
    setDeletedSubQuestions((prev) => [
      ...prev,
      ...deletedItems.filter(({ id }) => typeof id === 'number'),
    ]);
    updateQuestion({ subQuestions: subQuestionsClone });
  };

  const onColumnPaste = (event, value, index) => {
    const lines = convertPasteToValues(value);
    if (lines.length <= 1) return;

    event.preventDefault();

    const newColumns = lines.map((text) => ({
      id: nanoid(),
      richText: null,
      text,
    }));

    const answersClone = [...question.answers];
    const deletedItems = answersClone.splice(index, newColumns.length, ...newColumns);
    handleDeleteAnswers(deletedItems);
    updateQuestion({ answers: answersClone });
  };

  const onImageUpload = async (file, index, isSubQuestion = false) => {
    setImageError(false, index, isSubQuestion);
    setImageLoading(true, index, isSubQuestion);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });
      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({
          fields: imageObject.fields,
          file,
          url: imageObject.url,
        });
      }
      setUploadedImage(imageObject, index, isSubQuestion);
      setImageLoading(false, index, isSubQuestion);
    } catch (error) {
      setImageError(true, index, isSubQuestion);
      setImageLoading(false, index, isSubQuestion);
      throw error; // passes error back to RichTextInput handler
    }
  };

  const onSave = async () => {
    setErrorSubmitting(null);
    setIsSubmitting(true);

    const {
      concepts,
      image,
      noneAnswer,
      properties,
      richText,
      showConceptImage,
      subQuestions,
      text,
      type,
    } = question;
    let answers = [...question.answers];

    if (noneAnswer) answers.push(noneAnswer);

    if (
      !QUESTION_TYPES_WITH_OPTIONS.includes(type) &&
      type !== QUESTION_TYPES['TABLE-RADIO'].value
    ) {
      // mark any existing answers as _delete: true
      answers = answers
        .filter(({ id }) => typeof id === 'number')
        .map((answer) => ({ _delete: true, ...answer }));
    }

    const controller = new AbortController();
    try {
      let data;
      const questionPayload = {
        answers,
        concepts,
        image,
        properties,
        richText,
        showConceptImage,
        subQuestions,
        text,
        type,
      };
      const questionId =
        (question.id !== null && question.id !== undefined) || !shouldPersistOnCreate
          ? (question.id ?? nanoid())
          : undefined;
      const questionData = {
        ...questionPayload,

        answers: [...answers, ...deletedAnswers.map((answer) => ({ _delete: true, ...answer }))],
        ...(questionId && { id: questionId }),
        subQuestions: [
          ...subQuestions,
          ...deletedSubQuestions.map((subQuestion) => ({
            _delete: true,
            ...subQuestion,
          })),
        ],
      };

      if (question.id !== null && question.id !== undefined && shouldPersistOnUpdate) {
        data = await updateQuestionApi({
          question: questionData,
          signal: controller.signal,
        });
      } else if ((question.id === null || question.id === undefined) && shouldPersistOnCreate) {
        data = await createQuestion({
          question: {
            ...questionData,
            folder: FOLDER_ID_CUSTOM,
            instantSurveyTypes: [instantSurveyType.id],
          },
          signal: controller.signal,
        });
      } else {
        data = questionData;
      }
      if (isEditing) return onUpdate(data, carryForwardLogic);
      return onCreate(data, carryForwardLogic);
    } catch (error) {
      if (!controller.signal.aborted) {
        setErrorSubmitting(error);
        setIsSubmitting(false);
      }
    }
  };

  const onSubQuestionDragEnd = (result) => {
    if (!result?.destination) return;

    const subQuestions = [...question.subQuestions];
    const [removed] = subQuestions.splice(result?.source?.index, 1);
    subQuestions.splice(result?.destination?.index, 0, removed);
    updateQuestion({ subQuestions });
  };

  const setImageError = (value, index, isSubQuestion = false) => {
    setHasImageError((prev) => {
      if (isSubQuestion) {
        const subQuestions = [...prev.subQuestions];
        subQuestions[index] = value;
        return { ...prev, subQuestions };
      } else if (index !== undefined) {
        const answers = [...prev.answers];
        answers[index] = value;
        return { ...prev, answers };
      } else {
        return { ...prev, question: value };
      }
    });
  };

  const setImageLoading = (value, index, isSubQuestion = false) => {
    setIsImageLoading((prev) => {
      if (isSubQuestion) {
        const subQuestions = [...prev.subQuestions];
        subQuestions[index] = value;
        return { ...prev, subQuestions };
      } else if (index !== undefined) {
        const answers = [...prev.answers];
        answers[index] = value;
        return { ...prev, answers };
      } else {
        return { ...prev, question: value };
      }
    });
  };

  const setUploadedImage = (value, index, isSubQuestion = false) => {
    if (isSubQuestion) {
      updateSubQuestion(question.subQuestions[index].id, { image: value });
    } else if (index !== undefined) {
      const answerId = question.answers[index].id;
      updateAnswer(answerId, { image: value });
    } else {
      updateQuestion({ image: value });
    }
  };

  const updateAnswer = (answerId, changes) => {
    setQuestion((prev) => ({
      ...prev,
      answers: prev.answers.map((answer) =>
        answer.id === answerId ? { ...answer, ...changes } : answer
      ),
    }));
  };

  const updateQuestion = (changes) => {
    setQuestion((prev) => ({
      ...prev,
      ...changes,
    }));
  };

  const updateSubQuestion = (subQuestionId, changes) => {
    setQuestion((prev) => ({
      ...prev,
      subQuestions: prev.subQuestions.map((subQuestion) =>
        subQuestion.id === subQuestionId ? { ...subQuestion, ...changes } : subQuestion
      ),
    }));
  };

  const updateMaxAnswersAfterDelete = () => {
    setQuestion((prev) => {
      const maxAnswers = Math.min(
        prev.properties.maxAnswers,
        prev.answers.length + (prev.noneAnswer ? 1 : 0)
      );

      return {
        ...prev,
        properties: {
          ...prev.properties,
          // set maxAnswers to undefined if all answers are deleted
          maxAnswers: maxAnswers || undefined,
          mustBeMax: maxAnswers ? prev.properties.mustBeMax : undefined,
        },
      };
    });
  };

  const newOptionTooltip = getNewOptionTooltip();
  const saveButtonTooltip = getSaveButtonTooltip();
  const actionButtons = [
    <Button
      data-testid="modal-cancel-button"
      key="modal-cancel-button"
      onClick={onClose}
      text="Cancel"
      variant="secondary"
    />,
    <OverlayTriggerTooltip content={saveButtonTooltip} key="modal-confirm-button" placement="top">
      <Button
        data-testid="modal-confirm-button"
        isDisabled={Boolean(saveButtonTooltip)}
        isLoading={isSubmitting}
        onClick={onSave}
        text={isEditing ? 'Update' : 'Create & Add'}
      />
    </OverlayTriggerTooltip>,
  ];

  const pipingQuestionNumbersById = useMemo(
    () => getPipingQuestionNumbersById(activeQuestions),
    [activeQuestions]
  );

  const surveyQuestionsForPiping = useMemo(
    () =>
      getSurveyQuestionsForPiping({
        canPipingResponseTypes,
        pipingQuestionNumbersById,
        questionDisplayIndexById,
        questionsBeforeCurrent,
      }),
    [
      canPipingResponseTypes,
      pipingQuestionNumbersById,
      questionDisplayIndexById,
      questionsBeforeCurrent,
    ]
  );

  const mergedQuestionVariables =
    instantSurveyType?.id === SURVEY_TYPES.NEW_ITEM
      ? [...availableQuestionVariables, ...newItemAvailableQuestionVariables]
      : availableQuestionVariables;

  const hasVariableInGridColumns =
    isGridType &&
    question.answers.some(({ text }) =>
      mergedQuestionVariables.some((variable) => text?.includes(`[${variable}]`))
    );

  const variablesMention = {
    data: mergedQuestionVariables.map((variable) => ({
      display: variable,
      id: variable,
    })),
    displayTransform: (_, display) => `[${display.toLowerCase()}]`,
    icon: <SerifXIcon />,
    key: 'variables',
    label: 'Variable',
    markup: '[__display__]',
    tooltip: 'Add a Variable',
    trigger: '[',
  };
  const pipingMention = {
    data: surveyQuestionsForPiping,
    displayTransform: (id) => `{${id}}`,
    icon: <PencilV2Icon />,
    key: 'questions',
    label: 'Prev Answer',
    markup: '{__id__}',
    tooltip: 'Add a Previous Answer',
    trigger: '{',
  };

  const mentions = surveyQuestionsForPiping.length
    ? [variablesMention, pipingMention]
    : [variablesMention];

  const suggestions = {
    description: 'Use our dynamic variables to help with recalls for your surveys.',
    helpUrl: instantSurveyType.helpUrl,
    title: 'Search for Variables',
  };

  return (
    <Modal
      buttons={isTypeUnselected ? [] : actionButtons}
      className={styles['modal-instant-survey-question']}
      isActive
      onClose={onClose}
      sidebar={
        <Sidebar
          allowedQuestionTypes={allowedQuestionTypes}
          canSelectQuestionType={!isCarryForwardSource}
          isVideoSurvey={isVideoSurvey}
          onSelectQuestionType={handleSelectQuestionType}
          question={question}
        />
      }
      size="large"
      title={`${isEditing ? 'Edit' : 'Create'} Survey Question`}
    >
      {isTypeUnselected ? (
        <TypeUnselectedPlaceholder />
      ) : (
        <Layout.Flex.Column gap="x-large">
          <Layout.Flex.Column gap="small">
            {errorSubmitting && (
              <Announcement
                className={styles['modal-instant-survey-question-error-announcement']}
                text={
                  errorSubmitting?.response?.data?.error ||
                  `There was an error ${question.id ? 'updating' : 'creating'} your question.`
                }
                variant="error"
              />
            )}
            {isCarryForwardSource && (
              <Announcement
                className={styles['modal-instant-survey-question-error-announcement']}
                data-testid="carry-forward-source-announcement"
                header="Carry Forward Source"
                icon={<ArrowBendDownRightIcon />}
                text="You cannot change this question type because it is being used as a source for carry forward logic in another question. Remove the carry forward logic before changing this question."
                variant="warn"
              />
            )}
            <div className={styles['modal-instant-survey-question-text-label']}>
              {currentQuestionType.icon}
              <h3>{currentQuestionType.name}</h3>
            </div>
            <RichTextInput
              autoFocus={question.id ? false : true}
              error={
                question.text.length > QUESTION_MAX_LENGTH
                  ? VALIDATION_MESSAGES.QUESTION_MAX_LENGTH_ERROR
                  : null
              }
              hasConceptName={hasConceptName}
              hasImageError={hasImageError.question}
              id="question-text-rich-input"
              imageControlsLeadingContent={
                hasConceptImage ? (
                  <DynamicConceptImageControl
                    groups={survey?.groups}
                    isDisabled={question.image != null}
                    isSelected={!!question.showConceptImage}
                    onChange={handleUpdateShowConceptImage}
                  />
                ) : null
              }
              imageControlsPlacement="below"
              imageUploadDisabledTooltip={
                isSequentialMonadicSubQuestion ? MONADIC_IMAGE_DISABLED_TOOLTIP : undefined
              }
              imageUrl={question.image?.cdnUrl}
              isExpanded
              isImageLoading={isImageLoading.question}
              isImageUploadDisabled={question.showConceptImage || isSequentialMonadicSubQuestion}
              isSingleLine={false}
              maxImageDimensionSize={MAX_IMAGE_DIMENSION_SIZE}
              maxImageSize={IMAGE_UPLOAD_MAX_FILE_SIZE}
              maxLength={QUESTION_MAX_LENGTH}
              mentions={mentions}
              onChange={(value) =>
                updateQuestion({
                  richText: value,
                  text: stripHtml(value),
                })
              }
              onImageRemove={setUploadedImage}
              onImageUpload={onImageUpload}
              placeholder="Enter question"
              setHasImageError={setImageError}
              suggestions={suggestions}
              value={question.richText || question.text}
            />
            <p className={styles['modal-instant-survey-question-type-description']}>
              {currentQuestionType.description}
            </p>
          </Layout.Flex.Column>

          {hasVariableInGridColumns && (
            <Announcement
              icon={<ExclamationTriangleIcon />}
              text="Using variables may make your question longer than expected, as each variable will be replaced with the respondent's actual value when the survey is delivered."
              variant="warn"
            />
          )}

          {isMaxDiff && (
            <Layout.Flex.Column gap="small">
              <MaxDiffAnnouncement question={question} />
              <MaxDiffWarningAnnouncement question={question} />
            </Layout.Flex.Column>
          )}

          <QuestionProperties
            carryForwardLogic={carryForwardLogic}
            onDeleteAnswers={handleDeleteAnswers}
            onDeleteSubQuestions={handleDeleteSubQuestions}
            onUpdateQuestion={handleUpdateQuestion}
            onUpdateQuestionProperties={handleUpdateQuestionProperties}
            question={question}
            questionsBeforeCurrent={questionsBeforeCurrent}
            setCarryForwardLogic={setCarryForwardLogic}
          />

          {QUESTION_TYPES_WITH_OPTIONS.includes(question.type) && (
            <div className={styles['modal-instant-survey-question-answer-options']}>
              <Layout.Flex.Column>
                <div className={styles['modal-instant-survey-question-answer-options-header']}>
                  <strong>Answer Options</strong>
                  <small>({ANSWER_MAX_LENGTH} characters)</small>
                </div>

                <DragDropContext onDragEnd={onAnswerDragEnd}>
                  <Droppable droppableId="modalSurveyQuestionAnswers">
                    {(provided) => (
                      <ul
                        className={styles['modal-instant-survey-question-answer-option-list']}
                        data-testid="answer-list"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {orderedAnswers.map((answer, index) => {
                          const answerInputError = getAnswerInputError({ answer, index });
                          const isDragDisabled =
                            question.answers.length === 1 ||
                            question.properties.randomizeOptions ||
                            isMaxDiff ||
                            isVanWestendorp ||
                            answer.isCarryForwardGenerated;

                          return (
                            <Draggable
                              draggableId={`answer-${answer.id}`} // requires a string
                              index={index}
                              isDragDisabled={isDragDisabled}
                              key={answer.id}
                            >
                              {(provided) => {
                                return (
                                  <li
                                    className={
                                      styles['modal-instant-survey-question-answer-option']
                                    }
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    tabIndex="-1"
                                  >
                                    <Answer
                                      answer={answer}
                                      isDraggable={!isDragDisabled}
                                      onAnchor={() =>
                                        updateAnswer(answer.id, {
                                          isAnchored: !answer.isAnchored,
                                        })
                                      }
                                      onDelete={() => deleteAnswer(index)}
                                      onExcludeFromCarryForward={() =>
                                        updateAnswer(answer.id, {
                                          excludeFromCarryForward: !answer.excludeFromCarryForward,
                                        })
                                      }
                                      onMakeExclusive={() =>
                                        updateAnswer(answer.id, {
                                          isExclusive: !answer.isExclusive,
                                        })
                                      }
                                      type={question.type}
                                    >
                                      {answer.isCarryForwardGenerated ? (
                                        <Layout.Flex.Column
                                          className={
                                            styles[
                                              'modal-instant-survey-question-answer-carry-forward'
                                            ]
                                          }
                                          gap="x-small"
                                        >
                                          <Layout.Flex gap="x-small">
                                            <div
                                              className={
                                                styles[
                                                  'modal-instant-survey-question-answer-carry-forward-icon'
                                                ]
                                              }
                                            >
                                              <ArrowBendDownRightIcon />
                                            </div>
                                            <Layout.Fill
                                              dangerouslySetInnerHTML={{
                                                __html: sanitizeRichText(
                                                  answer.richText || answer.text
                                                ),
                                              }}
                                            />
                                          </Layout.Flex>
                                          {answer.image?.cdnUrl && (
                                            <div
                                              className={
                                                styles[
                                                  'modal-instant-survey-question-answer-carry-forward-image'
                                                ]
                                              }
                                            >
                                              <img
                                                alt="Carry Forward Answer"
                                                src={answer.image.cdnUrl}
                                              />
                                            </div>
                                          )}
                                        </Layout.Flex.Column>
                                      ) : (
                                        <RichTextInput
                                          error={answerInputError}
                                          hasImageError={hasImageError.answers[index]}
                                          imageUploadDisabledTooltip={
                                            isSequentialMonadicSubQuestion
                                              ? MONADIC_IMAGE_DISABLED_TOOLTIP
                                              : undefined
                                          }
                                          imageUrl={answer.image?.cdnUrl}
                                          isDisabled={!useCalypsoProvider && answer?.isOther}
                                          isImageLoading={isImageLoading.answers[index]}
                                          isImageUploadDisabled={isSequentialMonadicSubQuestion}
                                          isImageUploadHidden={isVanWestendorp}
                                          maxImageDimensionSize={MAX_IMAGE_DIMENSION_SIZE}
                                          maxImageSize={IMAGE_UPLOAD_MAX_FILE_SIZE}
                                          maxLength={ANSWER_MAX_LENGTH}
                                          mentions={mentions}
                                          onChange={(value) =>
                                            updateAnswer(answer.id, {
                                              richText: value,
                                              text: stripHtml(value),
                                            })
                                          }
                                          onImageRemove={() => setUploadedImage(null, index)}
                                          onImageUpload={(file) => onImageUpload(file, index)}
                                          onPaste={(event, value) =>
                                            onAnswerPaste(event, value, index)
                                          }
                                          placeholder="Enter answer"
                                          setHasImageError={(value) => setImageError(value, index)}
                                          suggestions={suggestions}
                                          value={answer.richText || answer.text}
                                        />
                                      )}
                                    </Answer>
                                  </li>
                                );
                              }}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>

                {question.noneAnswer && (
                  <ul
                    className={styles['modal-instant-survey-question-none-option']}
                    data-testid="other-none-list"
                  >
                    <li>
                      <Answer
                        answer={question.noneAnswer}
                        onDelete={deleteNoneAnswer}
                        type={question.type}
                      >
                        <div className={styles['modal-instant-survey-question-none']}>
                          <div>{question.noneAnswer.text}</div>
                          {(isContSum || isRankType) && (
                            <ErrorText>{VALIDATION_MESSAGES.NO_NONE_OF_THE_ABOVE}</ErrorText>
                          )}
                        </div>
                      </Answer>
                    </li>
                  </ul>
                )}

                {!isVanWestendorp && (
                  <div className={styles['modal-instant-survey-question-actions']}>
                    <PlusIcon />
                    <OverlayTriggerTooltip content={newOptionTooltip} placement="top">
                      <ButtonText
                        data-testid="add-answer-button"
                        disabled={Boolean(newOptionTooltip)}
                        onClick={addAnswer}
                      >
                        <strong>New</strong>&nbsp;Option
                        {canAddOther || canAddNone ? ',' : null}
                      </ButtonText>
                    </OverlayTriggerTooltip>
                    {canAddOther && (
                      <ButtonText data-testid="add-other-button" onClick={addOtherAnswer}>
                        <strong>&quot;{otherPleaseSpecify}&quot;</strong>&nbsp;Option
                        {!question.noneAnswer ? ',' : null}
                      </ButtonText>
                    )}
                    {canAddNone && (
                      <ButtonText data-testid="add-none-button" onClick={addNoneAnswer}>
                        <strong>&quot;{noneOfTheAbove}&quot;</strong>&nbsp;Option
                      </ButtonText>
                    )}
                  </div>
                )}
              </Layout.Flex.Column>
            </div>
          )}
          {isVanWestendorp && (
            <div className={styles['modal-instant-survey-question-reset-wording']}>
              <RepromptV2Icon />
              <ButtonText
                onClick={() =>
                  handleUpdateQuestion({
                    answers: question.answers.map((answer, index) => ({
                      ...answer,
                      richText: null,
                      text: VW_STANDARD_PROMPTS[index] ?? answer.text,
                    })),
                    richText: null,
                    text: VW_STANDARD_STEM,
                  })
                }
              >
                Reset to standard wording
              </ButtonText>
            </div>
          )}

          {isGridType && (
            <div className={styles['modal-instant-survey-question-answer-options']}>
              <Layout.Flex.Column gap="x-large">
                <Layout.Flex.Column>
                  <div className={styles['modal-instant-survey-question-answer-options-header']}>
                    <strong>Column Headers</strong>
                    <small>({ANSWER_MAX_LENGTH} characters)</small>
                  </div>
                  <Layout.Flex
                    className={styles['modal-instant-survey-question-columns']}
                    gap="small"
                  >
                    {question.answers.map((answer, index) => (
                      <div
                        className={styles['modal-instant-survey-question-column-header']}
                        key={answer.id}
                      >
                        <ColumnsIcon />
                        <EditableText
                          aria-label="Column Header"
                          error={
                            answer.text.length > ANSWER_MAX_LENGTH
                              ? VALIDATION_MESSAGES.GRID_COLUMN_LENGTH_ERROR
                              : null
                          }
                          maxLength={ANSWER_MAX_LENGTH}
                          onChange={(value) =>
                            updateAnswer(answer.id, { richText: null, text: value })
                          }
                          onClear={() => deleteAnswer(index)}
                          onPaste={(event, value) => onColumnPaste(event, value, index)}
                          value={answer.text}
                        />
                      </div>
                    ))}
                    <IconButton
                      className={styles['modal-instant-survey-question-column-header-button']}
                      isDisabled={answersLength >= QUESTION_GRID_MAX_COLUMNS}
                      onClick={() => addAnswer()}
                      tooltip={
                        answersLength < QUESTION_GRID_MAX_COLUMNS ? (
                          'Add Column'
                        ) : (
                          <>
                            <strong>Sorry</strong>. You have reached your column limit. Try removing
                            a column first.
                          </>
                        )
                      }
                    >
                      <PlusIcon />
                    </IconButton>
                  </Layout.Flex>
                  {answersLength > QUESTION_GRID_MAX_COLUMNS && (
                    <ErrorText>{VALIDATION_MESSAGES.GRID_MAX_COLUMNS_ERROR}</ErrorText>
                  )}
                </Layout.Flex.Column>

                <div className={styles['modal-instant-survey-question-grid-divider']} />
                <Layout.Flex.Column>
                  <div className={styles['modal-instant-survey-question-answer-options-header']}>
                    <strong>Question Rows</strong>
                    <small>({ANSWER_MAX_LENGTH} characters)</small>
                  </div>
                  {subQuestionDeleteError && (
                    <Announcement
                      className={styles['modal-instant-survey-question-error-announcement']}
                      text={subQuestionDeleteError}
                      variant="error"
                    />
                  )}
                  <GridAnnouncement conceptCount={conceptCount} question={question} />
                  <div>
                    <DragDropContext onDragEnd={onSubQuestionDragEnd}>
                      <Droppable droppableId="modalSurveyQuestionAnswers">
                        {(provided) => (
                          <ul ref={provided.innerRef} {...provided.droppableProps}>
                            {question.subQuestions.map((subQuestion, index) => {
                              const isDragDisabled =
                                question.subQuestions.length === 1 ||
                                question.properties?.randomizeSubQuestions ||
                                subQuestion.isCarryForwardGenerated;

                              return (
                                <Draggable
                                  draggableId={`sub_question-${subQuestion.id}`} // requires a string
                                  index={index}
                                  isDragDisabled={isDragDisabled}
                                  key={subQuestion.id}
                                >
                                  {(provided) => {
                                    return (
                                      <li
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        tabIndex="-1"
                                      >
                                        <SubQuestion
                                          index={index}
                                          isDraggable={!isDragDisabled}
                                          onDelete={() => deleteSubQuestion(index)}
                                          onExcludeFromCarryForward={() =>
                                            updateSubQuestion(subQuestion.id, {
                                              excludeFromCarryForward:
                                                !subQuestion.excludeFromCarryForward,
                                            })
                                          }
                                          subQuestion={subQuestion}
                                        >
                                          {subQuestion.isCarryForwardGenerated ? (
                                            <Layout.Flex.Column
                                              className={
                                                styles[
                                                  'modal-instant-survey-question-answer-carry-forward'
                                                ]
                                              }
                                              gap="x-small"
                                            >
                                              <Layout.Flex gap="x-small">
                                                <div
                                                  className={
                                                    styles[
                                                      'modal-instant-survey-question-answer-carry-forward-icon'
                                                    ]
                                                  }
                                                >
                                                  <ArrowBendDownRightIcon />
                                                </div>
                                                <Layout.Fill
                                                  dangerouslySetInnerHTML={{
                                                    __html: sanitizeRichText(
                                                      subQuestion.richText || subQuestion.text
                                                    ),
                                                  }}
                                                />
                                              </Layout.Flex>
                                              {subQuestion.image?.cdnUrl && (
                                                <div
                                                  className={
                                                    styles[
                                                      'modal-instant-survey-question-answer-carry-forward-image'
                                                    ]
                                                  }
                                                >
                                                  <img
                                                    alt="Carry Forward SubQuestion"
                                                    src={subQuestion.image.cdnUrl}
                                                  />
                                                </div>
                                              )}
                                            </Layout.Flex.Column>
                                          ) : (
                                            <RichTextInput
                                              error={getSubQuestionError({ index, subQuestion })}
                                              hasImageError={hasImageError.subQuestions[index]}
                                              imageUploadDisabledTooltip={
                                                isSequentialMonadicSubQuestion
                                                  ? MONADIC_IMAGE_DISABLED_TOOLTIP
                                                  : undefined
                                              }
                                              imageUrl={subQuestion.image?.cdnUrl}
                                              isImageLoading={isImageLoading.subQuestions[index]}
                                              isImageUploadDisabled={isSequentialMonadicSubQuestion}
                                              maxImageDimensionSize={MAX_IMAGE_DIMENSION_SIZE}
                                              maxImageSize={IMAGE_UPLOAD_MAX_FILE_SIZE}
                                              maxLength={ANSWER_MAX_LENGTH}
                                              mentions={mentions}
                                              onChange={(value) =>
                                                updateSubQuestion(subQuestion.id, {
                                                  richText: value,
                                                  text: stripHtml(value),
                                                })
                                              }
                                              onImageRemove={() =>
                                                setUploadedImage(null, index, true)
                                              }
                                              onImageUpload={(file) =>
                                                onImageUpload(file, index, true)
                                              }
                                              onPaste={(event, value) =>
                                                onSubQuestionPaste(event, value, index)
                                              }
                                              placeholder="Enter row"
                                              setHasImageError={(value) =>
                                                setImageError(value, index, true)
                                              }
                                              suggestions={suggestions}
                                              value={subQuestion.richText || subQuestion.text}
                                            />
                                          )}
                                        </SubQuestion>
                                      </li>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </ul>
                        )}
                      </Droppable>
                    </DragDropContext>
                    {subQuestionsLength > QUESTION_GRID_MAX_ROWS && (
                      <ErrorText>{VALIDATION_MESSAGES.GRID_MAX_ROWS_ERROR}</ErrorText>
                    )}
                  </div>

                  <div className={styles['modal-instant-survey-question-actions']}>
                    <PlusIcon />
                    <OverlayTriggerTooltip
                      content={
                        subQuestionsLength >= QUESTION_GRID_MAX_ROWS
                          ? `To provide the best experience for our panelists on mobile, this question is limited to ${QUESTION_GRID_MAX_ROWS} rows. If you have more, please create a new question.`
                          : null
                      }
                      placement="right"
                    >
                      <ButtonText
                        data-testid="add-answer-button"
                        disabled={subQuestionsLength >= QUESTION_GRID_MAX_ROWS}
                        onClick={addSubQuestion}
                      >
                        <strong>New</strong>&nbsp;Option
                      </ButtonText>
                    </OverlayTriggerTooltip>
                  </div>
                </Layout.Flex.Column>
              </Layout.Flex.Column>
            </div>
          )}

          {isVideo && <VideoSurveyInstructions />}
        </Layout.Flex.Column>
      )}
    </Modal>
  );
};

ModalInstantSurveyQuestion.defaultProps = {
  allowedQuestionTypes: null,
  conceptCount: undefined,
  getQuestionCostForValidation: getQuestionCost,
  isDuplicate: false,
  isSequentialMonadicSubQuestion: false,
  shouldPersistOnCreate: true,
  shouldPersistOnUpdate: true,
};

ModalInstantSurveyQuestion.propTypes = {
  allowedQuestionTypes: PropTypes.arrayOf(PropTypes.object),
  conceptCount: PropTypes.number,
  getQuestionCostForValidation: PropTypes.func,
  isDuplicate: PropTypes.bool,
  isSequentialMonadicSubQuestion: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  originalQuestion: PropTypes.object,
  pipingQuestionsMap: PropTypes.object.isRequired,
  shouldPersistOnCreate: PropTypes.bool,
  shouldPersistOnUpdate: PropTypes.bool,
};

export default ModalInstantSurveyQuestion;
